import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Phone, 
  Mail, 
  MessageSquare, 
  Clock,
  Send,
  CheckCircle,
  AlertTriangle,
  MapPin
} from 'lucide-react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', phone: '', email: '' });
    }, 3000);
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent(`Bonjour, je souhaite être rappelé pour la Résidence NATOMA aux Almadies.
    
Nom: ${formData.name || '[À renseigner]'}
Téléphone: ${formData.phone || '[À renseigner]'}
Email: ${formData.email || '[À renseigner]'}

Je suis intéressé par un appartement F5 de 250m².`);
    window.open(`https://wa.me/221787172828?text=${message}`, '_blank');
  };

  return (
    <section id="contact" className="py-20 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-6 bg-amber-600 text-white border-amber-600">
            <Phone className="h-4 w-4 mr-2" />
            Contact Final
          </Badge>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Réservez votre appartement
            <br />
            <span className="text-amber-400">dès aujourd'hui</span>
          </h2>
          
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-8">
            Nos conseillers vous rappellent sous 24h pour finaliser 
            votre projet d'acquisition aux Almadies.
          </p>

          {/* Urgency Alert */}
          <Alert className="max-w-md mx-auto bg-red-50 border-red-200 mb-8">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800 font-semibold">
              ⚡ Seulement 3 appartements disponibles
            </AlertDescription>
          </Alert>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-white text-slate-800">
              <CardHeader>
                <CardTitle className="text-2xl text-center">
                  Formulaire de Contact Prioritaire
                </CardTitle>
                <p className="text-slate-600 text-center">
                  Remplissez ce formulaire pour être rappelé en priorité
                </p>
              </CardHeader>
              <CardContent>
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-slate-800 mb-2">Demande envoyée !</h3>
                    <p className="text-slate-600 mb-4">
                      Merci pour votre intérêt. Notre équipe vous contactera sous 24h.
                    </p>
                    <Badge className="bg-green-100 text-green-800">
                      ✅ Vous serez rappelé en priorité
                    </Badge>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Nom complet *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Votre nom complet"
                        required
                        className="w-full h-12 text-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Téléphone *
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+221 XX XXX XX XX"
                        required
                        className="w-full h-12 text-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="votre@email.com"
                        required
                        className="w-full h-12 text-lg"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Button 
                        type="submit"
                        size="lg"
                        className="bg-amber-600 hover:bg-amber-700 text-white h-14 text-lg font-semibold"
                      >
                        <Send className="h-5 w-5 mr-2" />
                        Être rappelé sous 24h
                      </Button>
                      <Button 
                        type="button"
                        onClick={openWhatsApp}
                        size="lg"
                        className="bg-green-600 hover:bg-green-700 text-white h-14 text-lg font-semibold"
                      >
                        <MessageSquare className="h-5 w-5 mr-2" />
                        WhatsApp direct
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            {/* SOGI Contact */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-amber-400 flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2" />
                  SOGI SUARL
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-amber-400" />
                  <div>
                    <a href="tel:+221787172828" className="text-white hover:text-amber-400 transition-colors block">
                      +221 78 717 28 28
                    </a>
                    <a href="tel:+221338677389" className="text-slate-300 hover:text-amber-400 transition-colors block text-sm">
                      +221 33 867 73 89
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-amber-400" />
                  <a 
                    href="mailto:gestion@sogi.sn" 
                    className="text-white hover:text-amber-400 transition-colors"
                  >
                    gestion@sogi.sn
                  </a>
                </div>
                
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-amber-400 mt-0.5" />
                  <div className="text-slate-300">
                    <div>Liberté 6 Extension, Dakar</div>
                    <div>Sénégal</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Business Hours */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-amber-400 flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  Horaires
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-300">Lundi - Vendredi</span>
                    <span className="text-white font-medium">8h00 - 18h00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-300">Samedi</span>
                    <span className="text-white font-medium">8h00 - 13h00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-300">Dimanche</span>
                    <span className="text-white font-medium">Fermé</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Price Reminder */}
            <Card className="bg-amber-600 text-white">
              <CardContent className="p-6 text-center">
                <h4 className="text-xl font-bold mb-2">Prix de Lancement</h4>
                <div className="text-3xl font-bold mb-1">250 000 000</div>
                <div className="text-lg">FCFA</div>
                <p className="text-amber-100 text-sm mt-2">
                  Conditions préférentielles VEFA
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom Notice */}
        <div className="text-center mt-16">
          <div className="bg-slate-800 rounded-xl p-6 max-w-2xl mx-auto">
            <h4 className="text-lg font-semibold text-amber-400 mb-2">
              Garantie de Rappel sous 24h
            </h4>
            <p className="text-slate-300">
              Notre équipe commerciale s'engage à vous recontacter dans les 24 heures 
              suivant votre demande, du lundi au samedi.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;